// src/hooks/useAgentFormSync.ts

import { IconName } from "@components/AnimalCard";
import { useEffect } from "react";
import { useWatch } from "react-hook-form";
import {
    syncAgentApprovedApplications,
    syncAgentApprovedPetsDetails,
    syncAgentConsent,
    syncLandlordDetails
} from "../hooks/formSync";


type DynamicItem = {
  type: IconName;
  uid: number;
  pet_id?: string | null;
};

type UseAgentFormSyncProps = {
  items: DynamicItem[];
  agentApprovedlandlord: any;
  agentApprovedApplications: any;
  agentApprovedPetsDetails: any[];
  setValue: any;
  watch: any;
  reset: any;
  control: any;
  trigger?: (name?: string | string[]) => Promise<boolean>; // Optional: form trigger for deferred re-validation
  skipConsentSync?: boolean; // Optional: skip consent syncing if page handles it inline
};

export function useAgentFormSync({
  items,
  agentApprovedlandlord,
  agentApprovedApplications,
  agentApprovedPetsDetails,
  setValue,
  watch,
  reset,
  control,
  trigger,
  skipConsentSync = false
}: UseAgentFormSyncProps) {
  // Watch consent field values to trigger re-renders when they change
  // Only set up watchers if consent sync is not skipped
  const consentValues = useWatch({
    control,
    name: skipConsentSync ? [] : items.map(item => `${item.type}.agent_consent.${item.uid}`)
  });

  const agentConditions = useWatch({
    control,
    name: skipConsentSync ? [] : items.map(item => `${item.type}.agent_consent_conditions.${item.uid}`)
  });

  useEffect(() => {
    // Skip consent sync if the page handles it inline (e.g., Agent page)
    if (skipConsentSync) return;
    const cleared = syncAgentConsent(items, watch, setValue);
    // Deferred re-validation: after clearing fields with shouldValidate:false,
    // isValid becomes stale. We only trigger the consent fields (which always
    // have a value at this point) to avoid showing validation messages on
    // untouched fields. Any trigger() call recalculates isValid for the entire form.
    if (trigger && cleared) {
      const consentFieldNames = items.map(item => `${item.type}.agent_consent.${item.uid}`);
      setTimeout(() => trigger(consentFieldNames), 0);
    }
  }, [items, consentValues, agentConditions, setValue, skipConsentSync, trigger]);

  useEffect(() => {
    syncLandlordDetails(agentApprovedlandlord, reset);
  }, [agentApprovedlandlord, reset]);

  useEffect(() => {
    syncAgentApprovedApplications(agentApprovedApplications, setValue);
  }, [agentApprovedApplications, setValue]);

  useEffect(() => {
    syncAgentApprovedPetsDetails(agentApprovedPetsDetails, items, setValue);
  }, [agentApprovedPetsDetails, items, setValue]);
}